import { useState } from 'react'
import { api, type Constraint, type Guidelines, type Portfolio, type Rule } from '../api'
import { FundPicker, useFunds } from '../components/FundPicker'
import { NotEvaluated } from '../components/NotEvaluated'
import { Origin } from '../components/Origin'
import { says, subjectKeyText } from '../limits'
import { useDesks } from '../desks'

/**
 * Side-by-side comparison — proposal §D.
 *
 * The point of the screen is the *differences*, which is why alignment matters
 * more than layout. Rows are keyed on `(measure, basis, subject_key)` — the same
 * triple §6.3 groups by — so two portfolios' limits line up only when they
 * constrain the same thing in the same terms.
 *
 * **A blank cell means "no rule recorded", and the screen says so rather than
 * leaving an empty box.** An empty cell reads as *unconstrained*, and the
 * difference between *this fund has no such limit* and *nobody wrote one down*
 * is the entire distinction between a compliance statement and a record.
 *
 * **Rows where the vocabulary differs cannot align, and that is the finding.**
 * If one portfolio says `duration` and another `duration_years`, they appear as
 * two rows with one blank each — which is exactly what proposal §8's dictionary
 * fragmentation risk looks like when it has already happened, and this is the
 * screen where somebody notices.
 *
 * **Two funds, chosen from lists, compared on a click.** It used to be a search
 * box that added portfolios one at a time to an open-ended set, so the screen
 * loaded and re-rendered on every addition and a comparison of one fund with
 * itself was a state it could reach. Two pickers say what the screen is for; the
 * button says when the comparison happens, which is once, after both choices are
 * made rather than during them.
 *
 * **One fund at a time, in two steps** (decision 91). Somebody across the whole firm
 * chooses a desk and then a fund for the first place, then the same for the second,
 * and compares. Choosing both at once, each behind a desk, put four controls in one
 * row for a question asked one fund at a time.
 */
export function Compare() {
  const { funds, error: fundsError } = useFunds()
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  /** Which place is being chosen: the first fund, then the second. */
  const [step, setStep] = useState<1 | 2>(1)
  const { firmWide } = useDesks()
  const nameOf = (id: string) => funds.find((f) => f.id === id)
  const [shown, setShown] = useState<{ fund: Portfolio; data: Guidelines }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = Boolean(left) && Boolean(right) && left !== right

  async function compare() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const pair = await Promise.all(
        [left, right].map(async (id) => ({
          fund: funds.find((f) => f.id === id)!,
          data: await api.guidelines(id),
        })),
      )
      setShown(pair)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * What a row aligns on.
   *
   * A measured constraint aligns on what it constrains, which is the whole
   * argument of the screen. A narrative one cannot: the store now keys each
   * paragraph of prose on its own `rule_key` — two paragraphs are not one
   * constraint stated twice — and a migrated key carries the fund's own code
   * in front of the workbook's row label, so `vantoris.derivatives` and
   * `halven.derivatives` are the same row of the same workbook and would never
   * meet. The fund's own prefix is stripped, and only its own: a key that does
   * not start with the code it was read against is left alone rather than
   * guessed at.
   */
  function keyOf(constraint: Constraint, code: string): string {
    if (constraint.measure) {
      return `${constraint.measure}|${constraint.basis}|${constraint.subject_key}`
    }
    const prefix = `${code.toLowerCase()}.`
    const row = constraint.subject_key.toLowerCase().startsWith(prefix)
      ? constraint.subject_key.slice(prefix.length)
      : constraint.subject_key
    return `narrative|${row}`
  }

  function label(key: string): { measure: string; basis: string; subject: string } {
    if (key.startsWith('narrative|')) {
      return { measure: 'narrative', basis: '', subject: key.slice('narrative|'.length) }
    }
    const [measure, basis, subject] = key.split('|')
    return {
      measure: measure ?? '',
      basis: basis ? ` / ${basis}` : '',
      subject: subjectKeyText(subject ?? ''),
    }
  }

  // Every constraint either of the chosen portfolios has, in a stable order.
  const keys = [
    ...new Set(
      shown.flatMap((c) => c.data.constraints.map((x) => keyOf(x, c.fund.code))),
    ),
  ].sort()

  function cell(index: number, key: string): Rule | null {
    const side = shown[index]
    if (!side) return null
    const constraint = side.data.constraints.find(
      (x) => keyOf(x, side.fund.code) === key,
    )
    if (!constraint) return null
    return constraint.binding ?? constraint.also[0] ?? null
  }

  /** Whether the two sides say different things. The screen exists for these
   *  rows, so they are marked rather than left to be spotted. */
  function differs(key: string): boolean {
    if (shown.length < 2) return false
    const [a, b] = [cell(0, key), cell(1, key)]
    if (!a || !b) return true
    return says(a).text !== says(b).text
  }

  return (
    <div className="compare">
      <header className="page-head">
        <h2>Compare two funds</h2>
        <p className="note">
          Two mandates side by side, so a difference between them is visible
          rather than remembered. A row where the two disagree is the interesting
          one — a limit one fund has and the other does not, or the same limit at
          two thresholds.
        </p>
      </header>

      <ol className="steps" data-testid="compare-steps">
        <li className={step === 1 ? 'current' : 'done'}>
          <h3>1. First fund</h3>
          {step === 1 ? (
            <div className="filters">
              <FundPicker
                label="Fund"
                value={left}
                onChange={setLeft}
                funds={funds}
                exclude={right}
                testId="left"
              />
              <button disabled={!left} onClick={() => setStep(2)}>
                Next
              </button>
            </div>
          ) : (
            <p className="chosen-fund" data-testid="first-chosen">
              <strong>{nameOf(left)?.code}</strong>
              {firmWide && nameOf(left)?.desk_code && <span className="clause">Desk {nameOf(left)?.desk_code}</span>}
              <button type="button" className="link-button" onClick={() => setStep(1)}>
                Change
              </button>
            </p>
          )}
        </li>
        <li className={step === 2 ? 'current' : 'todo'}>
          <h3>2. Second fund</h3>
          {step === 2 && (
            <div className="filters">
              <FundPicker
                label="Fund"
                value={right}
                onChange={setRight}
                funds={funds}
                exclude={left}
                testId="right"
              />
              <button disabled={!ready || busy} onClick={() => void compare()}>
                Compare
              </button>
            </div>
          )}
        </li>
      </ol>

      {(error || fundsError) && (
        <p className="error" role="alert">
          {error ?? fundsError}
        </p>
      )}

      {shown.length === 0 ? (
        <p className="empty">Choose the first fund, then the second, and compare them.</p>
      ) : (
        <>
          <NotEvaluated />
          {/* `has-menus` for the `?` explainers: `.grid` clips to its own
              rounded corners, which would cut a popover off at the table
              edge. */}
          <table className="grid comparison has-menus" data-testid="comparison">
            <thead>
              <tr>
                <th scope="col">Constrains</th>
                {shown.map((c) => (
                  <th key={c.fund.id} scope="col">
                    <code>{c.fund.code}</code>
                    {firmWide && c.fund.desk_code && <span className="clause">Desk {c.fund.desk_code}</span>}
                    {/* Only when it adds something. A fund imported from a
                        workbook column usually has the code for a name, and
                        printing both gave `HALVENHALVEN`. */}
                    {c.fund.name !== c.fund.code && (
                      <span className="clause">{c.fund.name}</span>
                    )}
                  </th>
                ))}
                <th scope="col">Same?</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => {
                const { measure, basis, subject } = label(key)
                const different = differs(key)
                return (
                  <tr key={key} data-testid="comparison-row">
                    <th scope="row">
                      {measure}
                      {basis && <span className="basis">{basis}</span>}
                      <span className="subject">{subject}</span>
                    </th>
                    {shown.map((c, index) => {
                      const rule = cell(index, key)
                      const said = rule ? says(rule) : null
                      return (
                        <td key={c.fund.id}>
                          {rule && said ? (
                            <>
                              <span className={said.numeric ? 'limit' : 'no-number'}>
                                {said.text}
                              </span>
                              {/* Only where it is a difference. `Origin` renders
                                  unconditionally on the lookup table, and the
                                  argument for that is good: a badge that comes
                                  and goes trains the reader to read its absence
                                  as "not labelled yet". It does not survive a
                                  grid, where *written for this fund* under every
                                  cell of every row is the same true sentence
                                  forty times and the column heading has already
                                  named the fund. What is worth a line here is
                                  the limit this fund did not write for itself. */}
                              {rule.inherited && <Origin rule={rule} />}
                            </>
                          ) : (
                            /* Never an empty box. */
                            <span className="absent" data-testid="absent">
                              no rule recorded
                            </span>
                          )}
                        </td>
                      )
                    })}
                    <td>
                      {different ? (
                        <span className="differs" data-testid="differs">
                          differs
                        </span>
                      ) : (
                        <span className="clause">same</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="note">
            Rows align on measure, basis and subject. A limit that appears on one
            portfolio and not another may be a real difference in the mandates —
            or two names for the same thing, which is worth resolving in the
            dictionary.
          </p>
        </>
      )}
    </div>
  )
}
